/**
 * /venues/:venueSlug/admin/seasons — the venue's seasons, past and present.
 *
 * A venue runs ONE season at a time. That is enforced in seasons/index.js
 * rather than by the schema, which is why the create button here is disabled
 * while one is open instead of opening a form that can only 409.
 *
 * Season status is a one-way street: active -> completed -> archived, and
 * nothing leads back. Completing freezes the standings, so the endpoint refuses
 * it while any night is unfinished — this page asks the season for its
 * tournaments BEFORE offering the confirmation, so the warning names the nights
 * instead of arriving as a rejection afterwards.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../lib/api.js';
import { useVenue } from '../context/AuthContext.jsx';
import { formatDateShort, formatAvg } from '../lib/format.js';

/**
 * Season status as a badge. A THIRD status vocabulary — StatusBadge speaks
 * tournaments (where 'active' is gold) and BillingBadge speaks venues. Here
 * 'active' is the season being played and reads as success, while 'completed'
 * is history rather than an achievement. Local to this page for the same reason
 * the other two are separate: one component holding three domains would have to
 * be told which one it is in on every use.
 */
const SEASON_LABELS = {
  active:    'Active',
  completed: 'Completed',
  archived:  'Archived',
};

const SEASON_VARIANTS = {
  active:    'success',
  completed: 'neutral',
  archived:  'neutral',
};

function SeasonBadge({ status }) {
  return (
    <span className={`hd-badge hd-badge-${SEASON_VARIANTS[status] ?? 'neutral'}`}>
      {SEASON_LABELS[status] ?? status}
    </span>
  );
}

/**
 * Mirrors STATUS_TRANSITIONS in seasons/[seasonId]/index.js.
 *
 * Unlike the venue endpoint, the seasons list does not return an
 * allowedTransitions field, so this cannot be driven from the server the way
 * PlatformVenueDetail's buttons are. If the server's table changes, this one
 * has to change with it.
 */
const TRANSITIONS = {
  active:    ['completed', 'archived'],
  completed: ['archived'],
  archived:  [],
};

const ACTION_LABELS = { completed: 'End season', archived: 'Archive' };

const CONFIRM_PROMPTS = {
  completed: 'Ending a season freezes its standings. Nothing leads back to active.',
  archived:  'Archiving files the season away. It cannot be reopened.',
};

const blankForm = () => ({ name: '', defaultLegs: 3, startDate: '', endDate: '' });

/* ------------------------------------------------------------------------- */

function CreateSeasonForm({ venueId, onCreated, onCancel }) {
  const [form, setForm] = useState(blankForm());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const { data, error: err } = await apiPost(`/api/venues/${venueId}/seasons`, {
      name:        form.name.trim(),
      defaultLegs: form.defaultLegs,
      // Blank dates are sent as null, which parseDate treats as "not set"
      // rather than as a malformed date.
      startDate:   form.startDate || null,
      endDate:     form.endDate   || null,
    });

    setBusy(false);

    if (err) { setError(err); return; }
    onCreated(data);
  };

  return (
    <form className="hd-card hd-admin-form hd-stack" onSubmit={onSubmit}>
      <h2 className="hd-section-title">Create season</h2>

      <div className="hd-field">
        <label className="hd-label" htmlFor="season-name">Name</label>
        <input
          id="season-name"
          className="hd-input"
          value={form.name}
          onChange={set('name')}
          placeholder="2026 Winter League"
          required
        />
      </div>

      <div className="hd-fields">
        <div className="hd-field">
          <label className="hd-label" htmlFor="season-legs">Default legs</label>
          <input
            id="season-legs"
            className="hd-input"
            type="number"
            min="1"
            value={form.defaultLegs}
            onChange={set('defaultLegs')}
          />
        </div>

        <div className="hd-field">
          <label className="hd-label" htmlFor="season-start">Start date</label>
          <input
            id="season-start"
            className="hd-input"
            type="date"
            value={form.startDate}
            onChange={set('startDate')}
          />
        </div>

        <div className="hd-field">
          <label className="hd-label" htmlFor="season-end">End date</label>
          <input
            id="season-end"
            className="hd-input"
            type="date"
            value={form.endDate}
            onChange={set('endDate')}
          />
          <span className="hd-hint">Optional — a season can run open-ended.</span>
        </div>
      </div>

      {error && <div className="hd-badge hd-badge-danger" role="alert">{error}</div>}

      <div className="hd-row">
        <button className="hd-btn hd-btn-primary" type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create season'}
        </button>
        <button className="hd-btn hd-btn-quiet" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------------- */

function EditSeasonForm({ venueId, season, onSaved, onCancel }) {
  const [form, setForm] = useState({
    name:        season.name,
    defaultLegs: season.defaultLegs ?? 3,
    startDate:   season.startDate ?? '',
    endDate:     season.endDate ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    // Status is deliberately absent: this form renames and redates, and mixing
    // a lifecycle move into a rename would let a typo close a season.
    const { error: err } = await apiPut(`/api/venues/${venueId}/seasons/${season.id}`, {
      name:        form.name.trim(),
      defaultLegs: form.defaultLegs,
      startDate:   form.startDate || null,
      endDate:     form.endDate   || null,
    });

    setBusy(false);

    if (err) { setError(err); return; }
    onSaved(`'${form.name.trim()}' updated.`);
  };

  return (
    <form className="hd-admin-form hd-stack" onSubmit={onSubmit}>
      <div className="hd-field">
        <label className="hd-label" htmlFor={`edit-name-${season.id}`}>Name</label>
        <input
          id={`edit-name-${season.id}`}
          className="hd-input"
          value={form.name}
          onChange={set('name')}
          required
        />
      </div>

      <div className="hd-fields">
        <div className="hd-field">
          <label className="hd-label" htmlFor={`edit-legs-${season.id}`}>Default legs</label>
          <input
            id={`edit-legs-${season.id}`}
            className="hd-input"
            type="number"
            min="1"
            value={form.defaultLegs}
            onChange={set('defaultLegs')}
          />
        </div>

        <div className="hd-field">
          <label className="hd-label" htmlFor={`edit-start-${season.id}`}>Start date</label>
          <input
            id={`edit-start-${season.id}`}
            className="hd-input"
            type="date"
            value={form.startDate}
            onChange={set('startDate')}
          />
        </div>

        <div className="hd-field">
          <label className="hd-label" htmlFor={`edit-end-${season.id}`}>End date</label>
          <input
            id={`edit-end-${season.id}`}
            className="hd-input"
            type="date"
            value={form.endDate}
            onChange={set('endDate')}
          />
        </div>
      </div>

      {error && <div className="hd-badge hd-badge-danger" role="alert">{error}</div>}

      <div className="hd-row">
        <button className="hd-btn hd-btn-primary" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
        <button className="hd-btn hd-btn-quiet" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------------- */

/**
 * The confirmation for a status move.
 *
 * For 'completed' it waits on the season detail and counts the nights that have
 * not finished, because that is exactly what the endpoint will refuse on. The
 * button stays live either way — the server is the authority, and a stale count
 * here must not be able to block a legitimate close.
 */
function ConfirmTransition({ to, detail, busy, onConfirm, onCancel }) {
  const unfinished = to === 'completed' && detail?.status === 'ready'
    ? detail.data.tournaments.filter((t) => t.status !== 'completed')
    : [];

  return (
    <div className="hd-card hd-admin-form hd-stack">
      <p>{CONFIRM_PROMPTS[to]}</p>

      {to === 'completed' && detail?.status === 'loading' && (
        <p className="hd-muted">Checking this season's tournaments…</p>
      )}

      {unfinished.length > 0 && (
        <div className="hd-badge hd-badge-pending" role="alert">
          {unfinished.length} tournament{unfinished.length === 1 ? '' : 's'} not completed
          {' — '}
          {unfinished
            .slice(0, 4)
            .map((t) => (t.nightNumber != null ? `night ${t.nightNumber}` : `#${t.id}`))
            .join(', ')}
          {unfinished.length > 4 && ` and ${unfinished.length - 4} more`}
        </div>
      )}

      <div className="hd-row">
        <button className="hd-btn hd-btn-primary" type="button" disabled={busy} onClick={onConfirm}>
          {busy ? 'Saving…' : `Yes, ${ACTION_LABELS[to].toLowerCase()}`}
        </button>
        <button className="hd-btn hd-btn-quiet" type="button" disabled={busy} onClick={onCancel}>
          Back
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- */

function SeasonDetail({ venueId, seasonId, detail }) {
  if (!detail || detail.status === 'loading') {
    return <div className="hd-empty">Loading season…</div>;
  }

  if (detail.status === 'error') {
    return <p className="hd-muted">{detail.error}</p>;
  }

  const { tournaments, standings } = detail.data;

  return (
    <div className="hd-stack">
      <div className="hd-row-between">
        <h3 className="hd-section-title" style={{ margin: 0 }}>Standings</h3>

        {/* A plain link, not a fetch: the CSV comes back with a
            Content-Disposition and the cookie rides along on a same-origin
            GET. Nothing here has to touch the body. */}
        <a
          className="hd-btn hd-btn-secondary hd-btn-sm"
          href={`/api/venues/${venueId}/seasons/${seasonId}/export-standings`}
        >
          Export CSV
        </a>
      </div>

      {standings.length === 0 ? (
        <div className="hd-empty">No results in this season yet.</div>
      ) : (
        <div className="hd-table-wrap">
          <table className="hd-table hd-admin-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th>Player</th>
                <th className="hd-table-num">P</th>
                <th className="hd-table-num">Pts</th>
                <th className="hd-table-num">W</th>
                <th className="hd-table-num">L</th>
                <th className="hd-table-num">Legs</th>
                <th className="hd-table-num">Avg</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((row) => (
                <tr key={`${row.position}-${row.displayName}`}>
                  <td className="hd-muted">{row.position}</td>
                  <td>
                    {row.displayName ?? 'Unknown player'}
                    {row.isGuest && <span className="hd-guest-tag">guest</span>}
                  </td>
                  <td className="hd-table-num">{row.tournamentsPlayed}</td>
                  <td className="hd-table-num hd-standings-pts">{row.totalPoints}</td>
                  <td className="hd-table-num">{row.matchesWon}</td>
                  <td className="hd-table-num">{row.matchesLost}</td>
                  <td className="hd-table-num">{row.legsWon}–{row.legsLost}</td>
                  <td className="hd-table-num">{formatAvg(row.seasonAvg)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="hd-section-title">Tournaments</h3>

      {tournaments.length === 0 ? (
        <div className="hd-empty">No tournaments in this season yet.</div>
      ) : (
        <div className="hd-table-wrap">
          <table className="hd-table">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Night</th>
                <th>Date</th>
                <th>Status</th>
                <th className="hd-table-num">Entries</th>
              </tr>
            </thead>
            <tbody>
              {tournaments.map((t) => (
                <tr key={t.id}>
                  <td>{t.nightNumber != null ? `Night ${t.nightNumber}` : '—'}</td>
                  <td>{formatDateShort(t.date) ?? 'Date TBC'}</td>
                  <td className="hd-muted">{t.status}</td>
                  <td className="hd-table-num">{t.entryCount} / {t.maxEntries}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- */

export default function AdminSeasons() {
  const { venueSlug } = useParams();
  const contextVenue = useVenue(venueSlug);
  const contextId = contextVenue?.venue_id ?? null;

  const [state, setState] = useState({ status: 'loading' });
  const [venueId, setVenueId] = useState(contextId);
  const [creating, setCreating] = useState(false);

  // One open panel at a time, wherever it was opened from: { id, mode }, where
  // mode is 'detail' | 'edit' | a target status for a confirmation.
  const [panel, setPanel] = useState(null);
  const [details, setDetails] = useState({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  const loadSeasons = useCallback(async (id) => {
    const { data, error: err } = await apiGet(`/api/venues/${id}/seasons`);
    if (err) return { status: 'error', error: err };
    return {
      status:         'ready',
      seasons:        data?.seasons ?? [],
      activeSeasonId: data?.activeSeasonId ?? null,
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    (async () => {
      let id = contextId;

      // Platform staff hold no user_venue_roles row, so the auth context has no
      // entry for a venue they can still administer.
      if (id === null) {
        const { data, error: err } = await apiGet(`/api/venues/by-slug/${venueSlug}`);
        if (cancelled) return;

        if (err || !data?.venue) {
          setState({ status: 'error', error: err ?? 'Venue not found' });
          return;
        }
        id = data.venue.id;
      }

      if (cancelled) return;
      setVenueId(id);

      const next = await loadSeasons(id);
      if (!cancelled) setState(next);
    })();

    return () => { cancelled = true; };
  }, [venueSlug, contextId, loadSeasons]);

  /** Loads one season's detail, once. Cached by id. */
  const loadDetail = useCallback(async (seasonId) => {
    setDetails((d) => (d[seasonId] ? d : { ...d, [seasonId]: { status: 'loading' } }));

    const { data, error: err } = await apiGet(`/api/venues/${venueId}/seasons/${seasonId}`);

    setDetails((d) => ({
      ...d,
      [seasonId]: err ? { status: 'error', error: err } : { status: 'ready', data },
    }));
  }, [venueId]);

  const openPanel = (seasonId, mode) => {
    setError(null);
    setNotice(null);

    // Both the detail view and the end-of-season confirmation need the season's
    // tournaments; only fetch when they are not already held.
    if ((mode === 'detail' || mode === 'completed') && !details[seasonId]) {
      loadDetail(seasonId);
    }

    setPanel((p) => (p?.id === seasonId && p?.mode === mode ? null : { id: seasonId, mode }));
  };

  const refresh = async () => {
    setState(await loadSeasons(venueId));
  };

  const changeStatus = async (season, to) => {
    setBusy(true);
    setError(null);

    const { error: err } = await apiPut(
      `/api/venues/${venueId}/seasons/${season.id}`,
      { status: to }
    );

    setBusy(false);

    if (err) { setError(err); return; }

    setPanel(null);
    // The detail is now stale — its tournaments and standings are unchanged but
    // its season row is not, and it is cheap to fetch again when next opened.
    setDetails((d) => { const next = { ...d }; delete next[season.id]; return next; });
    setNotice(`'${season.name}' is now ${SEASON_LABELS[to].toLowerCase()}.`);
    await refresh();
  };

  const onSaved = async (message) => {
    setPanel(null);
    setNotice(message);
    setDetails({});
    await refresh();
  };

  if (state.status === 'loading') {
    return <div className="hd-empty">Loading…</div>;
  }

  if (state.status === 'error') {
    return (
      <div className="hd-stack">
        <h1 className="hd-page-title">Seasons</h1>
        <p className="hd-muted">{state.error}</p>
      </div>
    );
  }

  const { seasons, activeSeasonId } = state;
  const active = seasons.find((s) => s.id === activeSeasonId) ?? null;

  /** The confirm/edit panel for one season, rendered wherever it was opened. */
  const panelFor = (season) => {
    if (panel?.id !== season.id) return null;

    if (panel.mode === 'edit') {
      return (
        <EditSeasonForm
          venueId={venueId}
          season={season}
          onSaved={onSaved}
          onCancel={() => setPanel(null)}
        />
      );
    }

    if (panel.mode === 'completed' || panel.mode === 'archived') {
      return (
        <ConfirmTransition
          to={panel.mode}
          detail={details[season.id]}
          busy={busy}
          onConfirm={() => changeStatus(season, panel.mode)}
          onCancel={() => setPanel(null)}
        />
      );
    }

    return null;
  };

  const actionButtons = (season) => (
    <>
      <button
        type="button"
        className="hd-btn hd-btn-quiet hd-btn-sm"
        onClick={() => openPanel(season.id, 'edit')}
      >
        Edit
      </button>

      {(TRANSITIONS[season.status] ?? []).map((to) => (
        <button
          key={to}
          type="button"
          className={`hd-btn hd-btn-sm ${to === 'completed' ? 'hd-btn-secondary' : 'hd-btn-quiet'}`}
          onClick={() => openPanel(season.id, to)}
        >
          {ACTION_LABELS[to]}
        </button>
      ))}
    </>
  );

  return (
    <div className="hd-stack">
      <div className="hd-row-between">
        <div>
          <h1 className="hd-page-title">Seasons</h1>
          <p className="hd-meta">{contextVenue?.name ?? venueSlug}</p>
        </div>

        <div className="hd-row">
          {/* Disabled rather than hidden, with the reason stated: the endpoint
              409s while a season is open, and a button that cannot succeed is
              worse than one that explains itself. */}
          <button
            className="hd-btn hd-btn-primary"
            type="button"
            disabled={active !== null}
            title={active ? `'${active.name}' is still active` : undefined}
            onClick={() => { setCreating((open) => !open); setNotice(null); setError(null); }}
          >
            Create season
          </button>
        </div>
      </div>

      {active && (
        <p className="hd-hint">
          End or archive '{active.name}' before starting another — a venue runs
          one season at a time.
        </p>
      )}

      {notice && <div className="hd-badge hd-badge-success" role="status">{notice}</div>}
      {error  && <div className="hd-badge hd-badge-danger"  role="alert">{error}</div>}

      {creating && (
        <CreateSeasonForm
          venueId={venueId}
          onCancel={() => setCreating(false)}
          onCreated={async (data) => {
            setCreating(false);
            setNotice(`'${data?.season?.name ?? 'Season'}' created.`);
            await refresh();
          }}
        />
      )}

      {/* ---- The season being played ------------------------------------- */}
      {active && (
        <section className="hd-season-active hd-stack">
          <div className="hd-row">
            <h2 className="hd-section-title" style={{ margin: 0 }}>{active.name}</h2>
            <SeasonBadge status={active.status} />
          </div>

          <div className="hd-venue-next-meta">
            {formatDateShort(active.startDate) ?? 'No start date'}
            {active.endDate && <> – {formatDateShort(active.endDate)}</>}
            {' · '}
            {active.tournamentCount} tournament{active.tournamentCount === 1 ? '' : 's'}
            {' · '}
            {active.entryCount} entr{active.entryCount === 1 ? 'y' : 'ies'}
          </div>

          <div className="hd-row">
            <button
              type="button"
              className="hd-btn hd-btn-secondary"
              onClick={() => openPanel(active.id, 'detail')}
            >
              {panel?.id === active.id && panel.mode === 'detail'
                ? 'Hide standings'
                : 'View standings'}
            </button>
            {actionButtons(active)}
          </div>

          {panelFor(active)}
        </section>
      )}

      {/* ---- Every season -------------------------------------------------- */}
      {seasons.length === 0 ? (
        <div className="hd-empty">
          No seasons yet. Create your first season to start running tournaments.
        </div>
      ) : (
        <section>
          <h2 className="hd-section-title">All seasons</h2>

          <div className="hd-table-wrap">
            <table className="hd-table hd-admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Start</th>
                  <th>End</th>
                  <th className="hd-table-num">Tournaments</th>
                  <th className="hd-table-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {seasons.map((season) => {
                  // The active season's edit and confirm panels render in its
                  // own block above, so the expanded row here would be empty.
                  const open = panel?.id === season.id
                    && (panel.mode === 'detail' || season.id !== activeSeasonId);

                  return [
                    <tr key={season.id}>
                      <td>
                        <button
                          type="button"
                          className="hd-link-button"
                          onClick={() => openPanel(season.id, 'detail')}
                        >
                          {season.name}
                        </button>
                      </td>
                      <td><SeasonBadge status={season.status} /></td>
                      <td>{formatDateShort(season.startDate) ?? '—'}</td>
                      <td>{formatDateShort(season.endDate) ?? '—'}</td>
                      <td className="hd-table-num">{season.tournamentCount}</td>
                      <td className="hd-table-right">
                        <div className="hd-row" style={{ justifyContent: 'flex-end' }}>
                          {actionButtons(season)}
                        </div>
                      </td>
                    </tr>,

                    // The expanded region is a second row spanning the table.
                    open && (
                      <tr key={`${season.id}-panel`}>
                        <td colSpan={6} style={{ paddingBottom: 24 }}>
                          {panel.mode === 'detail'
                            ? (
                              <SeasonDetail
                                venueId={venueId}
                                seasonId={season.id}
                                detail={details[season.id]}
                              />
                            )
                            : panelFor(season)}
                        </td>
                      </tr>
                    ),
                  ];
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
